import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Star, Award, TrendingUp, ChevronDown, ChevronUp, User, BarChart3 } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";
import { OverallTrendChart, ParamBreakdownChart } from "../../components/PerformanceTrendChart";

const PARAMS = ["productivity", "quality", "teamwork", "communication", "punctuality"];
const PARAM_LABELS = { productivity: "Productivity", quality: "Quality of Work", teamwork: "Teamwork", communication: "Communication", punctuality: "Punctuality" };

const RatingBar = ({ label, value }) => (
  <div className="flex items-center gap-2">
    <span className="text-xs text-slate-500 w-28">{label}</span>
    <div className="flex-1 h-1.5 bg-slate-100 rounded-full overflow-hidden">
      <div className="h-full rounded-full transition-all" style={{ width: `${(value / 5) * 100}%`, backgroundColor: value >= 4 ? "#22c55e" : value >= 3 ? "#f59e0b" : "#ef4444" }} />
    </div>
    <span className="text-xs font-semibold text-slate-600 w-6 text-right">{value}</span>
  </div>
);

const MyPerformance = () => {
  const [reviews, setReviews] = useState([]);
  const [trendData, setTrendData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [reviewsRes, trendRes] = await Promise.all([
        api.get("/self-service/performance"),
        api.get("/self-service/performance/trend"),
      ]);
      setReviews(reviewsRes.data);
      setTrendData(trendRes.data);
    } catch { toast.error("Failed to fetch performance data"); }
    finally { setLoading(false); }
  };

  const avgRating = reviews.length > 0
    ? (reviews.reduce((s, r) => s + (r.overall_rating || 0), 0) / reviews.length).toFixed(1) : "0.0";

  return (
    <div className="space-y-6" data-testid="my-performance-page">
      <div>
        <h1 className="text-2xl font-bold text-slate-800 font-heading">My Performance</h1>
        <p className="text-slate-500">Your monthly performance reviews</p>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
        <Card className="stat-card"><CardContent className="p-0">
          <div className="flex items-center gap-4"><div className="p-3 rounded-xl bg-primary/10"><Star className="h-6 w-6 text-primary" /></div>
            <div><p className="text-2xl font-bold text-slate-800">{reviews.length}</p><p className="text-sm text-slate-500">Total Reviews</p></div></div>
        </CardContent></Card>
        <Card className="stat-card"><CardContent className="p-0">
          <div className="flex items-center gap-4"><div className="p-3 rounded-xl bg-amber-100"><Award className="h-6 w-6 text-amber-700" /></div>
            <div><p className="text-2xl font-bold text-slate-800">{avgRating}</p><p className="text-sm text-slate-500">Avg Rating</p></div></div>
        </CardContent></Card>
        <Card className="stat-card"><CardContent className="p-0">
          <div className="flex items-center gap-4"><div className="p-3 rounded-xl bg-green-100"><TrendingUp className="h-6 w-6 text-green-700" /></div>
            <div><p className="text-2xl font-bold text-slate-800">{reviews.length > 0 ? reviews[0].overall_rating : "—"}</p><p className="text-sm text-slate-500">Latest Rating</p></div></div>
        </CardContent></Card>
      </div>

      {/* Trend Charts */}
      {trendData?.trend?.length > 1 && (
        <Card className="card-soft" data-testid="my-trend-section">
          <CardHeader>
            <CardTitle className="font-heading text-lg flex items-center gap-2">
              <BarChart3 className="h-5 w-5 text-primary" /> My Performance Trend
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            <div>
              <p className="text-xs font-semibold text-slate-500 uppercase mb-3">Overall Rating vs Department Average</p>
              <OverallTrendChart data={trendData.trend} />
            </div>
            <div>
              <p className="text-xs font-semibold text-slate-500 uppercase mb-3">Parameter Breakdown Over Time</p>
              <ParamBreakdownChart data={trendData.trend} />
            </div>
          </CardContent>
        </Card>
      )}

      <Card className="card-soft" data-testid="my-reviews-list">
        <CardHeader><CardTitle className="font-heading text-lg">Review History</CardTitle></CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center py-12"><div className="animate-spin h-8 w-8 border-4 border-primary border-t-transparent rounded-full"></div></div>
          ) : reviews.length === 0 ? (
            <div className="text-center py-12 text-slate-500">No approved performance reviews yet.</div>
          ) : (
            <div className="space-y-3">
              {reviews.map(review => {
                const expanded = expandedId === review.id;
                return (
                  <div key={review.id} className="border border-slate-100 rounded-xl hover:shadow-sm transition-shadow" data-testid={`my-review-${review.id}`}>
                    <div className="flex items-center gap-4 p-4 cursor-pointer" onClick={() => setExpandedId(expanded ? null : review.id)}>
                      <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                        <Star className="h-5 w-5 text-primary" />
                      </div>
                      <div className="flex-1">
                        <h3 className="font-semibold text-slate-800 text-sm">{review.review_period}</h3>
                        <p className="text-xs text-slate-400">Reviewed by: {review.reviewer_name || "—"}</p>
                      </div>
                      <div className="text-right shrink-0">
                        <p className="text-lg font-bold text-slate-700">{review.overall_rating}/5</p>
                      </div>
                      {expanded ? <ChevronUp className="h-4 w-4 text-slate-400" /> : <ChevronDown className="h-4 w-4 text-slate-400" />}
                    </div>
                    {expanded && (
                      <div className="px-4 pb-4 border-t border-slate-50 pt-3 space-y-3">
                        <div className="space-y-1">
                          {PARAMS.map(p => <RatingBar key={p} label={PARAM_LABELS[p]} value={review.ratings?.[p] || 0} />)}
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                          {review.achievements && <div><p className="text-[10px] font-semibold text-slate-400 uppercase mb-0.5">Achievements</p><p className="text-slate-600">{review.achievements}</p></div>}
                          {review.areas_of_improvement && <div><p className="text-[10px] font-semibold text-slate-400 uppercase mb-0.5">Areas to Improve</p><p className="text-slate-600">{review.areas_of_improvement}</p></div>}
                          {review.goals && <div><p className="text-[10px] font-semibold text-slate-400 uppercase mb-0.5">Goals</p><p className="text-slate-600">{review.goals}</p></div>}
                          {review.comments && <div><p className="text-[10px] font-semibold text-slate-400 uppercase mb-0.5">Comments</p><p className="text-slate-600">{review.comments}</p></div>}
                          {review.hr_comments && <div><p className="text-[10px] font-semibold text-slate-400 uppercase mb-0.5">HR Feedback</p><p className="text-slate-600">{review.hr_comments}</p></div>}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default MyPerformance;
